import { useNavigate } from '@tanstack/react-router';

import type { InsuranceType } from '../../../context/InsuranceTypeContext';
import { useInsuranceType } from '../../../hooks/useInsuranceType';
import { useTranslation } from '../../../hooks/useTranslation';
import { toast } from '../../../lib/toast';

export type FooterLink = {
  id: string;
  label: string;
  href: string;
  disabled?: boolean;
  insuranceType?: InsuranceType;
};

export type FooterColumn = {
  id: string;
  title: string;
  links: FooterLink[];
};

type TranslateFn = (key: string) => string;

function buildFooterData(t: TranslateFn) {
  return {
    tagline: t('home.footer.tagline'),
    columns: [
      {
        id: 'services',
        title: t('home.footer.services.title'),
        links: [
          {
            id: 'auto',
            label: t('home.footer.services.auto'),
            href: '/consent',
            disabled: false,
            insuranceType: 'auto' as InsuranceType,
          },
          {
            id: 'life',
            label: t('home.footer.services.life'),
            href: '/consent',
            disabled: true,
            insuranceType: 'life' as InsuranceType,
          },
          {
            id: 'home',
            label: t('home.footer.services.home'),
            href: '/consent',
            disabled: false,
            insuranceType: 'home' as InsuranceType,
          },
          {
            id: 'disability',
            label: t('home.footer.services.disability'),
            href: '/consent',
            disabled: true,
            insuranceType: 'disability' as InsuranceType,
          },
        ],
      },
      {
        id: 'company',
        title: t('home.footer.company.title'),
        links: [
          {
            id: 'about',
            label: t('home.footer.company.about'),
            href: '#benefits',
          },
          {
            id: 'contact',
            label: t('home.footer.company.contact'),
            href: '#benefits',
          },
        ],
      },
      {
        id: 'support',
        title: t('home.footer.support.title'),
        links: [
          {
            id: 'help',
            label: t('home.footer.support.help'),
            href: '#',
            disabled: true,
          },
          {
            id: 'faqs',
            label: t('home.footer.support.faqs'),
            href: '#',
            disabled: true,
          },
          {
            id: 'claims',
            label: t('home.footer.support.claims'),
            href: '#',
            disabled: true,
          },
          {
            id: 'privacy',
            label: t('home.footer.support.privacy'),
            href: '/privacy',
          },
        ],
      },
    ] as FooterColumn[],
    copyright: t('home.footer.copyright'),
  };
}

export const useFooter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSelectedType } = useInsuranceType();

  const data = buildFooterData(t);

  const handleServiceClick = (
    e: React.MouseEvent,
    link: FooterLink,
    columnId: string
  ) => {
    if (columnId !== 'services') return;

    e.preventDefault();

    if (link.disabled) {
      toast.info('toast.comingSoon', {
        messageParams: { service: link.label },
      });
      return;
    }

    if (link.insuranceType) {
      setSelectedType(link.insuranceType);
      navigate({ to: '/consent' });
    }
  };

  return { data, handleServiceClick };
};

export type FooterProps = ReturnType<typeof useFooter>;
